/**
 * JAISIU-3653 — sidebar-search jump-to-match helpers.
 *
 * When the operator clicks a row in the chat-history sidebar while a
 * search query is active, the chat should open AND visually focus the
 * first matching message. This module owns the two pure lookups that
 * power that flow:
 *
 *   - `findFirstMatchingMessageId(messages, query)`
 *       Walks an already-loaded transcript and returns the
 *       stable `messageId` of the first row whose `extractText`
 *       contains the query (case-insensitive). Used when the chat
 *       has at least one batch loaded into `chatMessagesBySession`.
 *
 *   - `findFirstMatchingMessageIdInDeepBatch(sessionKey, query)`
 *       Reads the ephemeral deep-batch index populated by
 *       `global-search-deep-batch.ts` (JAISIU-3652) for the same
 *       session key, and returns the first matching `messageId` from
 *       that walker. This is what makes "click → scroll" work even
 *       when the match lives in a batch the chat thread has never
 *       loaded: the search modal already paged it into module-scope
 *       memory; this helper makes that index navigable.
 *
 * Neither helper mutates any external state. The caller is
 * `fleet-chat-history-sidebar.ts`, which calls them at render time
 * to decide what messageId (if any) to pass to the click handler.
 * The actual centered-fetch + scroll-into-view is done by the
 * existing JAISIU-3533 anchor restore pipeline
 * (`restorePersistedScrollAnchor` in `app-chat-cursor-parity.ts`).
 *
 * No DOM, no Lit, no gateway. Pure data lookups so unit tests can
 * run in `vitest.node` without jsdom.
 */

import {
  inspectDeepBatchCache,
} from "./global-search-deep-batch.ts";
import { extractText } from "./message-extract.ts";

/**
 * CSS class added to the matched row when the chat opens, so the
 * operator's eye is drawn to the landing spot. Cleared on a timer
 * (see `JUMP_FOCUS_DURATION_MS`) or on the next session switch.
 *
 * The class is consumed by `chat-search-jump-focus` rules in
 * `fleet-theme.css` (added alongside the JAISIU-3655 highlight
 * classes so the visual language stays consistent).
 */
export const JUMP_FOCUS_CLASS = "chat-search-jump-focus";

/**
 * How long the focus pulse stays on screen. Long enough that the
 * operator notices it (matches scroll-into-view typically takes
 * ~300–600 ms), short enough that it does not feel sticky.
 */
export const JUMP_FOCUS_DURATION_MS = 2200;

/**
 * Minimum query length that triggers the "jump to first match"
 * behaviour. Mirrors the global-search modal's minimum; shorter
 * queries are treated as "not a real search" and the sidebar falls
 * back to the plain title-filter behaviour.
 */
export const SIDEBAR_JUMP_MIN_QUERY_LENGTH = 2;

/**
 * Best-effort id extraction from a single chat-history message.
 * Mirrors the helper in `app-chat-cursor-parity.ts` and
 * `global-search-deep-batch.ts` so the three lookup sites agree
 * on what "first messageId" means. Kept private so each consumer
 * gets the same answer regardless of who asks first.
 */
function readMessageId(message: unknown): string | null {
  if (!message || typeof message !== "object") return null;
  const record = message as {
    id?: unknown;
    transcriptId?: unknown;
    messageId?: unknown;
    message?: { id?: unknown };
  };
  for (const key of ["id", "transcriptId", "messageId"] as const) {
    const value = record[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  const inner = record.message;
  if (inner && typeof inner === "object") {
    const innerId = (inner as { id?: unknown }).id;
    if (typeof innerId === "string") {
      const trimmed = innerId.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
}

/**
 * Normalise a user-typed query to the form every lookup site uses:
 * trimmed and lower-cased. A query that is empty after trim returns
 * `null` so callers can short-circuit the entire pipeline without
 * having to repeat the empty-check at every call site.
 */
function normaliseQuery(query: string): string | null {
  // JAISIU-3655 — kept for backward compatibility (tests). Returns
  // null only when the query is below the minimum CONTENT length
  // (whitespace-only queries excluded). The returned needle is the
  // raw lowercased query with internal whitespace collapsed — NOT
  // trimmed at the edges — so callers that still rely on this
  // helper honour the "spaces are significant" contract.
  if (!hasMinimumContent(query, SIDEBAR_JUMP_MIN_QUERY_LENGTH)) return null;
  return query.toLowerCase().replace(/\s+/g, " ");
}

/** True when `query` has at least `min` non-whitespace characters.
 * Mirrors the gate every search site uses so a query like "can "
 * (3 letters + 1 space = 4 chars) passes a `min=2` gate the same
 * way "can" does. */
function hasMinimumContent(query: string, min: number): boolean {
  return query.replace(/\s+/g, "").length >= min;
}

/**
 * Scan an already-loaded transcript (one slice of
 * `app.chatMessagesBySession[sessionKey]`) and return the
 * `messageId` of the FIRST row whose `extractText` contains the
 * query. Returns `null` if:
 *
 *   - the query is empty / too short
 *   - the transcript is empty / not an array
 *   - no row's text contains the query
 *
 * The order of the input array is preserved — we do NOT sort or
 * re-order. The chat thread renders in chat order; the first match
 * in the array IS the first match the operator would see if they
 * scrolled top-down. Server-issued `id`s are stable so the result
 * can be passed straight to `restorePersistedScrollAnchor`.
 *
 * Exported for unit tests and for direct call from the sidebar
 * (which already has the loaded batch in memory).
 */
export function findFirstMatchingMessageId(
  messages: readonly unknown[] | null | undefined,
  query: string,
): string | null {
  // JAISIU-3655 — preserve leading / trailing whitespace so "can "
  // is distinct from "can" (operator requested). `normaliseQuery`
  // is now length-only — the actual needle is the lowercased,
  // internal-whitespace-collapsed query (matches what the sidebar
  // and chat view pass through).
  if (!hasMinimumContent(query, SIDEBAR_JUMP_MIN_QUERY_LENGTH)) return null;
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const needle = query.toLowerCase().replace(/\s+/g, " ");
  for (const message of messages) {
    const id = readMessageId(message);
    if (!id) continue;
    const text = extractText(message as Parameters<typeof extractText>[0]);
    if (!text) continue;
    // Apply the same internal-whitespace collapse on the haystack
    // so the match works when `extractText` joins parts with "\n".
    if (text.toLowerCase().replace(/\s+/g, " ").includes(needle)) {
      return id;
    }
  }
  return null;
}

/**
 * Look up the first matching messageId for `sessionKey` in the
 * ephemeral deep-batch index built by JAISIU-3652's
 * `global-search-deep-batch.ts`. The deep-batch walker accumulates
 * per-session text blobs (one blob per older `chat.history` page)
 * in module scope and exposes them through
 * `inspectDeepBatchCache()`. Each blob entry carries per-row
 * `messageId`s so we can resolve a hit back to a stable id without
 * re-running the server query.
 *
 * Returns `null` when:
 *
 *   - the query is empty / too short
 *   - no deep-batch entry exists for the session (the operator
 *     never opened the global-search modal, or the modal was
 *     closed and the cache cleared)
 *   - no row in any of the cached pages contains the query
 *
 * The lookup walks pages in CHRONOLOGICAL ORDER (oldest → newest)
 * so the returned id is the EARLIEST match in the chat, mirroring
 * "scroll up to first hit" in the chat-thread search counter.
 *
 * Exported for unit tests and direct call from the sidebar.
 */
export function findFirstMatchingMessageIdInDeepBatch(
  sessionKey: string,
  query: string,
): string | null {
  const needle = normaliseQuery(query);
  if (!needle) return null;
  if (!sessionKey) return null;
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) return null;
  const entry = inspectDeepBatchCache().get(trimmedKey);
  if (!entry) return null;
  // Pages are appended oldest → newest. Walking in iteration order
  // gives the chronological order we want.
  for (const page of entry.pages) {
    for (const row of page.rows) {
      if (!row.text) continue;
      // Apply the same internal-whitespace collapse on the
      // haystack so the match works when `extractText` joined the
      // row's content parts with "\n".
      if (row.text.toLowerCase().replace(/\s+/g, " ").includes(needle)) {
        return row.id;
      }
    }
  }
  return null;
}

/**
 * Combined lookup: try the live cache first, fall back to the
 * deep-batch index. The sidebar uses this so the click handler
 * works for matches in either source — the operator does not care
 * where the match lived, they just want to land on it.
 *
 * Returns the messageId of the first match by chat-chronological
 * order (live cache is the newest slice; deep-batch is older).
 * Returns `null` only when neither source has a match.
 *
 * Exported for unit tests and for direct call from the sidebar.
 */
export function findFirstMatchingMessageIdAnywhere(
  sessionKey: string,
  messages: readonly unknown[] | null | undefined,
  query: string,
): string | null {
  const fromLive = findFirstMatchingMessageId(messages, query);
  if (fromLive) return fromLive;
  return findFirstMatchingMessageIdInDeepBatch(sessionKey, query);
}
